import { db } from '@/db/client';
import { getSupabase } from '@/lib/supabase';
import type { TeamPreset } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { encodeTeamPresetToRow } from '../codec';
import { getUserSyncState, setUserLastUploadedAt } from '../state';

export interface UploadStats {
  uploaded: number;
}

export async function uploadPendingTeamPresets(userId: UserId): Promise<UploadStats> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync team presets');
  }

  const { lastUploadedAt = 0 } = await getUserSyncState(userId, 'team_presets');
  // Get all team presets that were created/updated after lastUploadedAt
  const pending: TeamPreset[] = await db.teamPresets
    .filter((preset) => {
      if (preset.deletedAt) {
        // Include deleted presets if they were deleted after lastUploadedAt
        return preset.deletedAt > lastUploadedAt;
      }
      // Include non-deleted presets if they were created or updated after lastUploadedAt
      return preset.createdAt > lastUploadedAt || preset.updatedAt > lastUploadedAt;
    })
    .toArray();

  if (pending.length === 0) return { uploaded: 0 };

  const rows = pending.map((preset) => ({
    ...encodeTeamPresetToRow(preset),
    user_id: userId,
  }));
  const { error } = await client.from('team_presets').upsert(rows, { onConflict: 'id' });
  if (error) throw error;

  // Update lastUploadedAt to the max of created_at, updated_at, deleted_at
  const maxTs = Math.max(
    ...pending.map((p) => Math.max(p.createdAt, p.updatedAt, p.deletedAt ?? 0)),
    0,
  );
  if (maxTs > lastUploadedAt) {
    await setUserLastUploadedAt(userId, 'team_presets', maxTs);
  }
  return { uploaded: pending.length };
}
